import { CarrierVisitDirection } from '@planning/app/api'
import { ChangeTransportSubFormType } from '@planning/components/organisms/ChangeTransportSubForm'
import { OrderFormStoreType } from '@planning/pages/Order/V2/stores/OrderFormStore'
import { formatOog } from '@planning/utils/order-utils'
import { formatStringPosition } from '@tom-ui/utils'
import { action, makeObservable, observable } from 'mobx'
import { OrderCargoViewCardAttributes } from '../organisms/OrderCargoViewCard'
import { OrderTransportViewCardAttributes } from '../organisms/OrderTransportViewCard'

export class OrderReviewStore {
  cargoData: OrderCargoViewCardAttributes = {}
  transportData: OrderTransportViewCardAttributes = {}

  constructor() {
    makeObservable(this, {
      cargoData: observable,
      transportData: observable,

      setData: action,
      setCargoData: action,
      setTransportData: action,
    })
  }

  setData(data: OrderFormStoreType) {
    this.setCargoData(data)
    this.setTransportData(data)
  }

  setTransportData(data: OrderFormStoreType) {
    this.transportData = {
      inboundTransport: {
        direction: CarrierVisitDirection.Inbound,
        carrierType: data.inboundCargoDetails?.transport?.carrierType,
        carrierName: data.inboundCargoDetails?.transport?.carrierName,
        eta: data.inboundCargoDetails?.transport?.eta,
        portOfLoading: undefined,
        dischargeApproval: data.dischargeApproval,
        vesselStowagePosition: this.getTransportStowagePosition(
          data.inboundCargoDetails?.transport,
        ),
      },
      outboundTransport: {
        direction: CarrierVisitDirection.Outbound,
        carrierType: data.outboundCargoDetails?.transport?.carrierType,
        carrierName: data.outboundCargoDetails?.transport?.carrierName,
        eta: data.outboundCargoDetails?.transport?.eta,
        portOfDischarge: undefined,
        finalDestination: undefined,
        loadApproval: data.loadApproval,
        vesselStowagePosition: this.getTransportStowagePosition(
          data.outboundCargoDetails?.transport,
        ),
        isFinalPosition: data.outboundCargoDetails?.transport?.isFinalPosition,
      },
    }
  }

  setCargoData(data: OrderFormStoreType) {
    this.cargoData = {
      isEmpty: data.cargoDetails?.cargoDetails?.isEmpty ?? false,
      hasSeals: data.cargoDetails?.seals?.hasSeals,
      grossWeight: data.cargoDetails?.cargoDetails?.grossWeight?.toString(),
      vgm: data.cargoDetails?.cargoDetails?.vgm?.toString(),
      content: data.cargoDetails?.cargoDetails?.content,
      orderReference: data.referenceNumber,
      customerName: data.customer?.name,
      consignee: data.consignee,
      dangerousGoods: data.cargoDetails?.cargoDetails?.dangerousGoods?.isDangerous,
      oog: data.cargoDetails?.oog?.isOog,
      oogDimensions: formatOog({ ...data.cargoDetails?.oog, isOOG: data.cargoDetails?.oog?.isOog }),
      unloadingMethod: data.cargoDetails?.oog?.unloadingMethod,
      stowageInstruction: data.cargoDetails?.stowageInstruction,

      billOfLading: data.billOfLadding,
      shippingOrderReference: data.shippingOrder,
      releaseReference: data.releaseReference,

      origin: data.origin,
      inlandDestination: data.inlandDestination,
      finalDestination: data.finalDestination,
      portOfLoading: data.portOfLoading,
      portOfDischarge: data.portOfDischarge,

      isApprovedInbound: data.dischargeApproval,
      isApprovedOutbound: data.loadApproval,

      forwarderId: data.forwarderId,
      shipperId: data.shipperId,
      imoClasses: data.cargoDetails?.cargoDetails?.dangerousGoods?.imoClasses,
      otherLabels: data.cargoDetails?.cargoDetails?.dangerousGoods?.otherLabels,
      unNumbers: data.cargoDetails?.cargoDetails?.dangerousGoods?.unNumbers,
    }
  }

  private getTransportStowagePosition(transport?: ChangeTransportSubFormType) {
    if (!transport?.bay || !transport?.row || !transport?.tier) {
      return undefined
    }

    return formatStringPosition(transport.bay, transport.row, transport.tier)
  }
}
